import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Statistic, Typography, Spin, Table, Tag, Avatar, Space, Divider } from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  BankOutlined,
  RocketOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  PictureOutlined,
  PhoneOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  FundOutlined,
  DollarOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
  AppstoreOutlined,
  FileProtectOutlined,
  GiftOutlined,
  PlayCircleOutlined,
  MessageOutlined,
  ArrowRightOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    companies: 0,
    companiesPending: 0,
    companiesApproved: 0,
    associations: 0,
    associationsPending: 0,
    associationsApproved: 0,
    academia: 0,
    academiaPending: 0,
    academiaApproved: 0,
    startups: 0,
    startupsPending: 0,
    startupsApproved: 0,
    incubators: 0,
    incubatorsPending: 0,
    incubatorsApproved: 0,
    investors: 0,
    investorsPending: 0,
    investorsApproved: 0,
    consultants: 0,
    consultantsPending: 0,
    consultantsApproved: 0,
    pressReleases: 0,
    gallery: 0,
    contactSubmissions: 0,
    investorRequests: 0,
    members: 0,
    sliders: 0,
    topUtility: 0,
    policies: 0,
    schemes: 0,
  });
  const [attention, setAttention] = useState({
    newMessages: 0,
    pendingRequests: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Fetch counts from different endpoints
      const [
        companiesRes,
        associationsRes,
        academiaRes,
        startupsRes,
        incubatorsRes,
        investorsRes,
        consultantsRes,
        pressReleasesRes,
        galleryRes,
        contactRes,
        investorRequestsRes,
        membersRes,
        slidersRes,
        topUtilityRes,
        policiesRes,
        newMessagesRes,
        pendingRequestsRes,
      ] = await Promise.allSettled([
        axios.get('/api/admin/companies', config),
        axios.get('/api/admin/associations', config),
        axios.get('/api/admin/academia', config),
        axios.get('/api/admin/startups', config),
        axios.get('/api/admin/incubators', config),
        axios.get('/api/admin/investors', config),
        axios.get('/api/admin/consultants', config),
        axios.get('/api/press-releases', config),
        axios.get('/api/gallery', config),
        axios.get('/api/contact/messages', { ...config, params: { limit: 1000 } }),
        axios.get('/api/investor/requests', config),
        axios.get('/api/members', config),
        axios.get('/api/sliders', config),
        axios.get('/api/top-utility/config', config),
        axios.get('/api/policies/get_allpolicies', config),
        axios.get('/api/contact/messages', { ...config, params: { status: 'new', limit: 1 } }),
        axios.get('/api/contact/requests', { ...config, params: { status: 'pending', limit: 1 } }),
      ]);

      // Log responses for debugging
      console.log('Dashboard API Responses:', {
        companies: companiesRes.status === 'fulfilled' ? companiesRes.value.data : companiesRes.reason,
        associations: associationsRes.status === 'fulfilled' ? associationsRes.value.data : associationsRes.reason,
        academia: academiaRes.status === 'fulfilled' ? academiaRes.value.data : academiaRes.reason,
        startups: startupsRes.status === 'fulfilled' ? startupsRes.value.data : startupsRes.reason,
        incubators: incubatorsRes.status === 'fulfilled' ? incubatorsRes.value.data : incubatorsRes.reason,
        investors: investorsRes.status === 'fulfilled' ? investorsRes.value.data : investorsRes.reason,
        consultants: consultantsRes.status === 'fulfilled' ? consultantsRes.value.data : consultantsRes.reason,
        pressReleases: pressReleasesRes.status === 'fulfilled' ? pressReleasesRes.value.data : pressReleasesRes.reason,
        gallery: galleryRes.status === 'fulfilled' ? galleryRes.value.data : galleryRes.reason,
        contact: contactRes.status === 'fulfilled' ? contactRes.value.data : contactRes.reason,
        investorRequests: investorRequestsRes.status === 'fulfilled' ? investorRequestsRes.value.data : investorRequestsRes.reason,
        members: membersRes.status === 'fulfilled' ? membersRes.value.data : membersRes.reason,
        sliders: slidersRes.status === 'fulfilled' ? slidersRes.value.data : slidersRes.reason,
        topUtility: topUtilityRes.status === 'fulfilled' ? topUtilityRes.value.data : topUtilityRes.reason,
        policies: policiesRes.status === 'fulfilled' ? policiesRes.value.data : policiesRes.reason,
      });

      // Helper function to get array length safely
      const getCount = (res, key) => {
        if (res.status !== 'fulfilled') return 0;
        const data = res.value.data;
        if (!data) return 0;

        // Prefer the server's total count when the response is paginated
        // (array length would otherwise be capped at the page size)
        if (data.pagination?.totalItems !== undefined) {
          return data.pagination.totalItems;
        }

        // If data has the key (e.g., data.companies, data.pressReleases)
        if (data[key] && Array.isArray(data[key])) {
          return data[key].length;
        }

        // If data itself is an array
        if (Array.isArray(data)) {
          return data.length;
        }

        return 0;
      };

      // Helper to count how many records in a persona list have a given status
      const getStatusCount = (res, key, status) => {
        if (res.status !== 'fulfilled') return 0;
        const data = res.value.data;
        if (!data) return 0;

        const list = Array.isArray(data[key]) ? data[key] : (Array.isArray(data) ? data : []);
        return list.filter((item) => item.status === status).length;
      };

      setStats({
        companies: getCount(companiesRes, 'companies'),
        companiesPending: getStatusCount(companiesRes, 'companies', 'pending'),
        companiesApproved: getStatusCount(companiesRes, 'companies', 'approved'),
        associations: getCount(associationsRes, 'associations'),
        associationsPending: getStatusCount(associationsRes, 'associations', 'pending'),
        associationsApproved: getStatusCount(associationsRes, 'associations', 'approved'),
        academia: getCount(academiaRes, 'academia'),
        academiaPending: getStatusCount(academiaRes, 'academia', 'pending'),
        academiaApproved: getStatusCount(academiaRes, 'academia', 'approved'),
        startups: getCount(startupsRes, 'startups'),
        startupsPending: getStatusCount(startupsRes, 'startups', 'pending'),
        startupsApproved: getStatusCount(startupsRes, 'startups', 'approved'),
        incubators: getCount(incubatorsRes, 'incubators'),
        incubatorsPending: getStatusCount(incubatorsRes, 'incubators', 'pending'),
        incubatorsApproved: getStatusCount(incubatorsRes, 'incubators', 'approved'),
        investors: getCount(investorsRes, 'investors'),
        investorsPending: getStatusCount(investorsRes, 'investors', 'pending'),
        investorsApproved: getStatusCount(investorsRes, 'investors', 'approved'),
        consultants: getCount(consultantsRes, 'consultants'),
        consultantsPending: getStatusCount(consultantsRes, 'consultants', 'pending'),
        consultantsApproved: getStatusCount(consultantsRes, 'consultants', 'approved'),
        pressReleases: getCount(pressReleasesRes, 'pressReleases'),
        gallery: getCount(galleryRes, 'images'),
        contactSubmissions: getCount(contactRes, 'data'),
        investorRequests: getCount(investorRequestsRes, 'data'),
        members: getCount(membersRes, 'data'),
        sliders: getCount(slidersRes, 'data'),
        topUtility: topUtilityRes.status === 'fulfilled' && topUtilityRes.value.data?.data ? 1 : 0,
        policies: getCount(policiesRes, 'policies'),
        schemes: 0, // No API endpoint for schemes yet
      });

      setAttention({
        newMessages: getCount(newMessagesRes, 'data'),
        pendingRequests: getCount(pendingRequestsRes, 'data'),
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Companies',
      value: stats.companies,
      pending: stats.companiesPending,
      approved: stats.companiesApproved,
      icon: BankOutlined,
      color: '#3C007A',
      bgColor: '#f0ebf8',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Associations',
      value: stats.associations,
      pending: stats.associationsPending,
      approved: stats.associationsApproved,
      icon: TeamOutlined,
      color: '#722ed1',
      bgColor: '#f9f0ff',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Academia Partners',
      value: stats.academia,
      pending: stats.academiaPending,
      approved: stats.academiaApproved,
      icon: ExperimentOutlined,
      color: '#1890ff',
      bgColor: '#e6f7ff',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Startups',
      value: stats.startups,
      pending: stats.startupsPending,
      approved: stats.startupsApproved,
      icon: RocketOutlined,
      color: '#fa8c16',
      bgColor: '#fff7e6',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Incubators',
      value: stats.incubators,
      pending: stats.incubatorsPending,
      approved: stats.incubatorsApproved,
      icon: ExperimentOutlined,
      color: '#52c41a',
      bgColor: '#f6ffed',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Investors',
      value: stats.investors,
      pending: stats.investorsPending,
      approved: stats.investorsApproved,
      icon: FundOutlined,
      color: '#eb2f96',
      bgColor: '#fff0f6',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
    {
      title: 'Consultants',
      value: stats.consultants,
      pending: stats.consultantsPending,
      approved: stats.consultantsApproved,
      icon: UserOutlined,
      color: '#13c2c2',
      bgColor: '#e6fffb',
      prefix: '',
      suffix: '',
      isMainPersona: true,
    },
  ];

  const contentStats = [
    { label: 'Press Releases', value: stats.pressReleases, icon: FileTextOutlined, color: '#fa8c16' },
    { label: 'Gallery Items', value: stats.gallery, icon: PictureOutlined, color: '#1890ff' },
    { label: 'Contact Forms', value: stats.contactSubmissions, icon: PhoneOutlined, color: '#52c41a' },
  ];

  const quickStats = [
    { 
      title: 'Total Personas', 
      value: stats.companies + stats.associations + stats.academia + stats.startups + stats.incubators + stats.investors + stats.consultants,
      change: '+12.5%',
      isIncrease: true,
      icon: ShoppingOutlined,
      color: '#3C007A',
      description: '7 Main Platform Personas'
    },
    { 
      title: 'Business Entities', 
      value: stats.companies + stats.associations + stats.startups,
      change: '+8.3%',
      isIncrease: true,
      icon: BankOutlined,
      color: '#722ed1',
      description: 'Companies, Associations & Startups'
    },
    { 
      title: 'Support Network', 
      value: stats.incubators + stats.investors + stats.consultants + stats.academia,
      change: '+15.7%',
      isIncrease: true,
      icon: CheckCircleOutlined,
      color: '#1890ff',
      description: 'Academia, Incubators, Investors & Consultants'
    },
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '500px',
        background: '#fafafa'
      }}>
        <Spin size="large" />
        <Text style={{ marginTop: 20, fontSize: 16, color: '#8c8c8c', fontFamily: "'Rubik', sans-serif" }}>
          Loading dashboard analytics...
        </Text>
      </div>
    );
  }

  const totalEntities = stats.companies + stats.associations + stats.academia + stats.startups + 
                       stats.incubators + stats.investors + stats.consultants;

  return (
    <div style={{ fontFamily: "'Rubik', sans-serif", background: '#fafafa', padding: 0, margin: -24 }}>
      
      {/* Header Section */}
      <div style={{
        background: 'linear-gradient(135deg, #3C007A 0%, #6b0fa0 100%)',
        padding: '40px 32px',
        borderBottom: '1px solid #f0f0f0',
        marginBottom: 24,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Decorative circles */}
        <div style={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 200,
          height: 200,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute',
          bottom: -30,
          left: -30,
          width: 150,
          height: 150,
          background: 'rgba(255,255,255,0.1)',
          borderRadius: '50%',
        }} />
        
        <Row align="middle" justify="space-between" style={{ position: 'relative', zIndex: 1 }}>
          <Col>
            <Title level={2} style={{ 
              margin: 0, 
              fontFamily: "'Rubik', sans-serif",
              color: '#fff',
              fontWeight: 700,
              fontSize: 32,
              marginBottom: 8
            }}>
              Dashboard Overview
            </Title>
            <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 15 }}>
              Monitor your 7 main platform personas and key metrics
            </Text>
          </Col>
          <Col>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: '12px 24px',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}>
              <div style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#52c41a',
                animation: 'pulse 2s infinite'
              }} />
              <Text style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
                Live Data
              </Text>
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ padding: '0 32px 32px' }}>

        {/* Needs Attention - Contact Submissions */}
        <Row gutter={[20, 20]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12}>
            <Card
              bordered={false}
              onClick={() => navigate('/admin/contact-submissions')}
              style={{
                borderRadius: 12,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                border: attention.newMessages > 0 ? '1px solid #ffd591' : '1px solid #f0f0f0',
                background: attention.newMessages > 0
                  ? 'linear-gradient(135deg, #fff7e6 0%, #fff1e0 100%)'
                  : '#fff',
                transition: 'all 0.2s ease',
              }}
              bodyStyle={{ padding: '24px' }}
              hoverable
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: attention.newMessages > 0 ? '#fff1e0' : '#f0ebf8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <MessageOutlined style={{ fontSize: 22, color: attention.newMessages > 0 ? '#fa8c16' : '#3C007A' }} />
                  </div>
                  <div>
                    <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: 14, color: '#8c8c8c', display: 'block' }}>
                      Website Messages
                    </Text>
                    <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: 24, color: attention.newMessages > 0 ? '#fa8c16' : '#262626' }}>
                      {attention.newMessages} new
                    </Text>
                  </div>
                </div>
                <ArrowRightOutlined style={{ color: attention.newMessages > 0 ? '#fa8c16' : '#bfbfbf' }} />
              </div>
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card
              bordered={false}
              onClick={() => navigate('/admin/contact-submissions')}
              style={{
                borderRadius: 12,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                cursor: 'pointer',
                border: attention.pendingRequests > 0 ? '1px solid #ffd591' : '1px solid #f0f0f0',
                background: attention.pendingRequests > 0
                  ? 'linear-gradient(135deg, #fff7e6 0%, #fff1e0 100%)'
                  : '#fff',
                transition: 'all 0.2s ease',
              }}
              bodyStyle={{ padding: '24px' }}
              hoverable
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: attention.pendingRequests > 0 ? '#fff1e0' : '#f0ebf8',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <TeamOutlined style={{ fontSize: 22, color: attention.pendingRequests > 0 ? '#fa8c16' : '#3C007A' }} />
                  </div>
                  <div>
                    <Text style={{ fontFamily: "'Rubik', sans-serif", fontSize: 14, color: '#8c8c8c', display: 'block' }}>
                      Contact AID Office
                    </Text>
                    <Text strong style={{ fontFamily: "'Rubik', sans-serif", fontSize: 24, color: attention.pendingRequests > 0 ? '#fa8c16' : '#262626' }}>
                      {attention.pendingRequests} pending
                    </Text>
                  </div>
                </div>
                <ArrowRightOutlined style={{ color: attention.pendingRequests > 0 ? '#fa8c16' : '#bfbfbf' }} />
              </div>
            </Card>
          </Col>
        </Row>

        {/* Main Personas Section - Highlighted */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: '32px',
          marginBottom: 24,
          boxShadow: '0 4px 16px rgba(60, 0, 122, 0.12)',
          border: '2px solid #3C007A15',
        }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 4,
                height: 24,
                background: 'linear-gradient(180deg, #3C007A 0%, #6b0fa0 100%)',
                borderRadius: 2
              }} />
              <Title level={3} style={{ 
                margin: 0, 
                fontFamily: "'Rubik', sans-serif", 
                fontWeight: 700,
                fontSize: 24,
                color: '#262626'
              }}>
                Platform Personas
              </Title>
              {/* <Tag color="#3C007A" style={{ 
                marginLeft: 8,
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 12px',
                borderRadius: 20
              }}>
                7 Main Categories
              </Tag> */}
            </div>
            {/* <Text style={{ 
              fontSize: 14, 
              color: '#8c8c8c',
              display: 'block',
              marginLeft: 16
            }}>
              Core user groups driving the platform ecosystem
            </Text> */}
          </div>

          <Row gutter={[20, 20]}>
            {statCards.map((card, index) => {
              const IconComponent = card.icon;
              return (
                <Col xs={24} sm={12} lg={12} xl={8} key={index}>
                  <Card
                    bordered={false}
                    style={{
                      background: '#fff',
                      borderRadius: 12,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      border: `1px solid ${card.color}`,
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    bodyStyle={{ padding: 0 }}
                    hoverable
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = `0 8px 24px ${card.color}30`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                    }}
                  >
                    <div style={{ padding: '28px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                        <div style={{
                          width: 56,
                          height: 56,
                          borderRadius: 12,
                          background: card.bgColor,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 4px 12px ${card.color}20`,
                        }}>
                          <IconComponent style={{ fontSize: 28, color: card.color }} />
                        </div>
                        
                        <div style={{
                          background: card.bgColor,
                          padding: '6px 14px',
                          borderRadius: 20,
                          border: `1px solid ${card.color}30`,
                        }}>
                          <Text style={{ 
                            fontSize: 11, 
                            color: card.color,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Persona {index + 1}
                          </Text>
                        </div>
                      </div>
                      
                      <Text style={{ 
                        fontSize: 13, 
                        color: '#8c8c8c',
                        display: 'block',
                        marginBottom: 8,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {card.title}
                      </Text>
                      
                      <Title level={1} style={{
                        margin: 0,
                        fontSize: 42,
                        fontWeight: 700,
                        color: card.color,
                        fontFamily: "'Rubik', sans-serif",
                        lineHeight: 1
                      }}>
                        {card.value}
                      </Title>

                      {card.isMainPersona && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          marginTop: 14,
                          paddingTop: 14,
                          borderTop: `1px solid ${card.color}15`,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#fa8c16', display: 'inline-block' }} />
                            <Text style={{ fontSize: 13, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
                              Pending: <strong style={{ color: '#fa8c16' }}>{card.pending || 0}</strong>
                            </Text>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#52c41a', display: 'inline-block' }} />
                            <Text style={{ fontSize: 13, color: '#595959', fontFamily: "'Rubik', sans-serif" }}>
                              Approved: <strong style={{ color: '#52c41a' }}>{card.approved || 0}</strong>
                            </Text>
                          </div>
                        </div>
                      )}
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>

        {/* Content & Communication Section */}
        <div style={{
          background: '#fff',
          borderRadius: 12,
          padding: '32px',
          marginBottom: 24,
          boxShadow: '0 4px 16px rgba(60, 0, 122, 0.12)',
          border: '2px solid #3C007A15',
        }}>
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <div style={{
                width: 4,
                height: 24,
                background: 'linear-gradient(180deg, #3C007A 0%, #6b0fa0 100%)',
                borderRadius: 2
              }} />
              <Title level={3} style={{ 
                margin: 0, 
                fontFamily: "'Rubik', sans-serif", 
                fontWeight: 700,
                fontSize: 24,
                color: '#262626'
              }}>
                Content & Communication
              </Title>
            </div>
          </div>

          <Table
            dataSource={[
              { key: 1, item: 'Executive Committee Members', count: stats.members, status: 'Active', icon: TeamOutlined, color: '#722ed1' },
              { key: 2, item: 'Slider', count: stats.sliders, status: 'Active', icon: PlayCircleOutlined, color: '#1890ff' },
              { key: 3, item: 'Top Header', count: stats.topUtility, status: 'Active', icon: AppstoreOutlined, color: '#fa8c16' },
              { key: 4, item: 'Media Gallery', count: stats.gallery, status: 'Active', icon: PictureOutlined, color: '#52c41a' },
              { key: 5, item: 'Press Releases', count: stats.pressReleases, status: 'Active', icon: FileTextOutlined, color: '#eb2f96' },
              { key: 6, item: 'Policy', count: stats.policies, status: 'Active', icon: FileProtectOutlined, color: '#13c2c2' },
              { key: 7, item: 'Contact Submissions', count: stats.contactSubmissions, status: 'Active', icon: PhoneOutlined, color: '#fa541c' },
              { key: 8, item: 'Investor Requests', count: stats.investorRequests, status: 'Active', icon: FundOutlined, color: '#eb2f96' },
            //   { key: 9, item: 'Schemes', count: stats.schemes, status: 'Active', icon: GiftOutlined, color: '#fa541c' },
            ]}
            columns={[
              {
                title: <Text style={{ fontWeight: 600, fontSize: 14, color: '#3C007A' }}>Content Type</Text>,
                dataIndex: 'item',
                key: 'item',
                render: (text, record) => {
                  const IconComponent = record.icon;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: 8,
                        background: `${record.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <IconComponent style={{ fontSize: 20, color: record.color }} />
                      </div>
                      <Text style={{ fontWeight: 500, fontSize: 14, color: '#262626' }}>
                        {text}
                      </Text>
                    </div>
                  );
                },
              },
              {
                title: <Text style={{ fontWeight: 600, fontSize: 14, color: '#3C007A' }}>Count</Text>,
                dataIndex: 'count',
                key: 'count',
                align: 'center',
                width: 120,
                render: (count, record) => (
                  <div style={{
                    display: 'inline-block',
                    minWidth: 60,
                    padding: '6px 16px',
                    borderRadius: 20,
                    background: count > 0 ? `${record.color}15` : '#f5f5f5',
                    border: `1px solid ${count > 0 ? `${record.color}30` : '#e8e8e8'}`
                  }}>
                    <Text style={{ 
                      fontSize: 16,
                      fontWeight: 700,
                      color: count > 0 ? record.color : '#8c8c8c',
                      fontFamily: "'Rubik', sans-serif"
                    }}>
                      {count}
                    </Text>
                  </div>
                ),
              },
              {
                title: <Text style={{ fontWeight: 600, fontSize: 14, color: '#3C007A' }}>Status</Text>,
                dataIndex: 'status',
                key: 'status',
                align: 'center',
                width: 120,
                render: (status) => (
                  <Tag 
                    color="#52c41a" 
                    style={{ 
                      fontSize: 13, 
                      borderRadius: 6,
                      fontWeight: 500,
                      padding: '4px 12px',
                      border: '1px solid #52c41a30'
                    }}
                  >
                    ✓ {status}
                  </Tag>
                ),
              },
            ]}
            pagination={false}
            size="middle"
            rowClassName={(record, index) => index % 2 === 0 ? '' : 'odd-row'}
            style={{
              fontFamily: "'Rubik', sans-serif"
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
